import { RefinementCtx, ZodIssueCode, z } from "zod";

import { weighScaleNewTicket } from "./weigh-scale-ticket.schema";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import { HUNDRED, ONE } from "@/constants/constants";
import {
  basicValidator,
  numberValidator,
  singleSelectValidator,
} from "@/lib/validators";

// Define a generic type for data
type ValidationData = {
  orderNumber?: string;
  customer?: string | null;
  project?: string | null;
};

const isArray = <T>(data: T | T[]): data is T[] => Array.isArray(data);

// validation function for `orderNumber` or array of data
const validateOrderNumber = <T extends ValidationData>(
  data: T | T[],
  ctx: RefinementCtx,
): void => {
  if (isArray(data)) {
    data.forEach((item) => validateOrderNumber(item, ctx));

    return;
  }

  const allEmpty = !data.orderNumber && !data.customer && !data.project;

  if (allEmpty) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message:
        "Either an order reference or both customer and project must be provided.",
      path: ["orderNumber"],
    });
  } else if (!data.orderNumber) {
    if (!data.customer) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: "Required",
        path: ["customer"],
      });
    }
    if (
      !data.project ||
      (typeof data.project === "string" && data.project.length === 0)
    ) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: "Required",
        path: ["project"],
      });
    }
  }
};

const createVehicleOnlySchema = z
  .object({
    orderNumber: z.string().optional(),
    customer: z.string().optional(),
    project: z.string().optional(),
    items: z.object({
      wasteType: singleSelectValidator(),
    }),
  })
  .superRefine((data, ctx) => {
    validateOrderNumber(data, ctx);
  });

const updateVehicleOnlySchema = z
  .object({
    id: z.string().optional(),
    orderNumber: z.string().optional(),
    customer: singleSelectValidator(false),
    defaultCustomerLabel: z.string().optional(),
    project: singleSelectValidator(false),
    defaultProjectLabel: z.string().optional(),
    items: z
      .object({
        id: z.string().optional(),
        wasteType: singleSelectValidator(),
        defaultWasteTypeLabel: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    validateOrderNumber(data, ctx);
  });

const baseItemSchema = z.object({
  wasteType: basicValidator(true, ERROR_MSG.REQUIRED, ONE, HUNDRED, true),
  defaultWasteTypeLabel: z.string().optional(),
  loadType: basicValidator(true, ERROR_MSG.REQUIRED, ONE, HUNDRED, true),
  defaultLoadTypeLabel: z.string().optional(),
  quantity: numberValidator({
    min: 1,
    messages: {
      min: ERROR_MSG.REQUIRED,
    },
  }),
});

const createItems = baseItemSchema;

const updateItem = baseItemSchema.extend({
  id: z.string().optional(),
});

const createAttachmentSchema = z.array(
  z
    .object({
      orderNumber: z.string().optional(),
      customer: z.string(),
      project: z.string(),
      items: z.array(createItems),
    })
    .superRefine((data, ctx) => {
      validateOrderNumber(data, ctx);
    }),
);

const updateAttachmentSchema = z.array(
  z
    .object({
      id: z.string().optional(),
      orderNumber: z.string().optional(),
      customer: singleSelectValidator(false),
      defaultCustomerLabel: z.string().optional(),
      project: singleSelectValidator(false),
      defaultProjectLabel: z.string().optional(),
      items: z.array(updateItem.or(createItems)),
      deleteWeighBridgeItems: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      validateOrderNumber(data, ctx);
    }),
);
export const createWeighBridgeSchema = z.object({
  vehicle: z.object({
    vehicleId: z.string().min(2, {
      message: "Required",
    }),
    grossWeight: numberValidator({
      min: 1,
    }),
    vehicleUser: z.string().min(2, {
      message: "Required",
    }),
  }),
  ticketType: z.enum(["VEHICLE_ONLY", "WITH_ATTACHMENT"], {
    message: "Required",
  }),
  tareWeight: z.coerce.number().min(0, {
    message: "Required",
  }),
  updatedTareWeight: z.coerce.number().optional(),
  notes: z.string().optional(),
  isDraft: z.boolean().default(false),
  details: z.union([createVehicleOnlySchema, createAttachmentSchema]),
  isTareWeightManual: z.string().default("true"),
});

export const updateWeighBridgeSchema = z.object({
  id: z.string(),
  vehicle: z
    .object({
      vehicleId: z.string().min(1, {
        message: "Required",
      }),
      defaultVehicleRegistration: z.string().optional(),
      grossWeight: z.coerce.number().min(0, {
        message: "Required",
      }),
      vehicleUser: z.string().min(1, {
        message: "Required",
      }),
    })
    .partial(),
  ticketType: z.enum(["VEHICLE_ONLY", "WITH_ATTACHMENT"], {
    message: "Required",
  }),
  details: z.union([updateVehicleOnlySchema, updateAttachmentSchema]),
  tareWeight: z.coerce.number().optional(),
  notes: z.string().optional(),
  isTareWeightManual: z.string().default("true"),
  deleteWeighBridgeDetails: z.array(z.string()).optional(),
  updatedTareWeight: z.coerce.number().optional(),
  isActionUpdate: z.boolean().optional().default(false),
});

export const multipleWeighScaleTicket = z.object({
  details: z.array(
    weighScaleNewTicket.extend({
      ticket: z.number().optional(),
    }),
  ),
});

export type TWeighScaleMultipleTicket = z.infer<
  typeof multipleWeighScaleTicket
>;
export type TVehicleOnly = z.infer<typeof createVehicleOnlySchema>;
export type TWithAttachment = z.infer<typeof createAttachmentSchema>;
export type TUpdateVehicleOnly = z.infer<typeof updateVehicleOnlySchema>;
export type TUpdateAttachment = z.infer<typeof updateAttachmentSchema>;

export type TCreateWeighBridge = z.infer<typeof createWeighBridgeSchema>;
export type TUpdateWeighBridge = z.infer<typeof updateWeighBridgeSchema>;
